import {
  BackButtonCWUI,
  CustomerCategoryTitleUI,
  ButtonUI,
} from '@next-order/next-order-components';
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import AnimationWrapper from 'components/AnimationWrapper';
import { Marker } from 'react-map-gl';
import Map from 'components/Map';
import { ReactComponent as MarkerThemedIcon } from 'assets/images/marker-themed.svg';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { reverseGeocodeAPI } from '../Address/actions';

export default function MapAdjustmentView({
  latitude,
  longitude,
  onBack,
  onSave,
}) {
  const storeConfig = useSelector(selectStoreConfig);
  const [currentLat, setCurrentLat] = useState(latitude);
  const [currentLng, setCurrentLng] = useState(longitude);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const initialLoad = useRef(true);
  const isMounted = useRef(true);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Calculate bounds for 500m radius around current position
  const bounds = useMemo(() => {
    const RADIUS_IN_METERS = 500;
    const EARTH_RADIUS = 6371000; // Earth radius in meters

    // Use initial coordinates for bounds to prevent glitching when marker moves
    const baseLat = latitude;
    const baseLng = longitude;

    // Convert meters to degrees (approximate)
    const latDelta = (RADIUS_IN_METERS / EARTH_RADIUS) * (180 / Math.PI);
    const lngDelta =
      (RADIUS_IN_METERS /
        (EARTH_RADIUS * Math.cos((baseLat * Math.PI) / 180))) *
      (180 / Math.PI);

    return [
      [baseLng - lngDelta, baseLat - latDelta], // Southwest corner
      [baseLng + lngDelta, baseLat + latDelta], // Northeast corner
    ];
  }, [latitude, longitude]);

  const handleSave = async () => {
    if (!isMounted.current) return;

    setIsLoading(true);
    try {
      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (isMounted.current) {
        onSave(currentLat, currentLng, currentAddress);
        // Check again after onSave in case it caused unmount
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('Error saving location:', error);
        setIsLoading(false);
      }
    }
  };

  // Helper function to check if coordinates are within bounds
  const isWithinBounds = useCallback(
    (lat, lng) => {
      const [southwest, northeast] = bounds;
      return (
        lng >= southwest[0] &&
        lng <= northeast[0] &&
        lat >= southwest[1] &&
        lat <= northeast[1]
      );
    },
    [bounds]
  );

  const onMarkerDragEnd = useCallback(
    async (event) => {
      if (!isMounted.current) return;

      const { lngLat } = event;
      const newLat = lngLat.lat;
      const newLng = lngLat.lng;

      if (isWithinBounds(newLat, newLng)) {
        initialLoad.current = false;
        setCurrentLng(newLng);
        setCurrentLat(newLat);
        setIsLoading(true);
        try {
          const data = await reverseGeocodeAPI({
            latitude: newLat,
            longitude: newLng,
            storeConfig,
          });

          if (isMounted.current) {
            const results = data?.results?.results;
            if (results?.length > 0) {
              setCurrentAddress(results[0]);
            }
          }
        } catch (error) {
          if (isMounted.current) {
            console.error('Failed to get address for new location', error);
            setCurrentAddress(null);
          }
        } finally {
          if (isMounted.current) {
            setIsLoading(false);
          }
        }
      }
    },
    [isWithinBounds, storeConfig]
  );

  const onMapClick = useCallback(
    async (event) => {
      if (!isMounted.current) return;

      const { lngLat } = event;
      const newLat = lngLat.lat;
      const newLng = lngLat.lng;

      if (isWithinBounds(newLat, newLng)) {
        initialLoad.current = false;
        setCurrentLng(newLng);
        setCurrentLat(newLat);
        setIsLoading(true);
        try {
          const data = await reverseGeocodeAPI({
            latitude: newLat,
            longitude: newLng,
            storeConfig,
          });

          if (isMounted.current) {
            const results = data?.results?.results;
            if (results?.length > 0) {
              setCurrentAddress(results[0]);
            }
          }
        } catch (error) {
          if (isMounted.current) {
            console.error('Failed to get address for new location', error);
            setCurrentAddress(null);
          }
        } finally {
          if (isMounted.current) {
            setIsLoading(false);
          }
        }
      }
    },
    [isWithinBounds, storeConfig]
  );

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI onClick={onBack} extendedClassName='mx-5' />

              <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                Adjust Pin
              </CustomerCategoryTitleUI>

              {/* Instructions */}
              <div className='mx-5 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                <p className='text-sm text-blue-800'>
                  Drag the pin or tap on the map to adjust your location
                </p>
              </div>

              <div className='mx-5 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg'>
                <p className='text-xs text-gray-600 truncate'>
                  {currentAddress
                    ? currentAddress.formatted_address
                    : 'Adjust pin to update address'}
                </p>
              </div>

              {/* Coordinates Display */}
              <div className='mx-5 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg'>
                <p className='text-xs text-gray-600'>
                  Latitude: {currentLat?.toFixed(6)} | Longitude:{' '}
                  {currentLng?.toFixed(6)}
                </p>
              </div>

              {/* Full Screen Interactive Map */}
              <div className='relative h-[calc(100%-320px)] mt-4 mx-5 rounded-xl overflow-hidden border border-gray-300'>
                <Map
                  lat={initialLoad.current ? currentLat : latitude}
                  long={initialLoad.current ? currentLng : longitude}
                  zoom={17}
                  showMarker={false}
                  onClick={onMapClick}
                  style={{ width: '100%', height: '100%' }}
                  cursor='crosshair'
                  maxBounds={bounds}
                >
                  <Marker
                    latitude={currentLat}
                    longitude={currentLng}
                    anchor='bottom'
                    draggable={true}
                    onDragEnd={onMarkerDragEnd}
                  >
                    <MarkerThemedIcon
                      width={45}
                      height={40}
                      style={{ cursor: 'pointer' }}
                    />
                  </Marker>
                </Map>
              </div>

              {/* Save Button */}
              <div className='fixed bottom-[75px] lg:bottom-0 left-0 right-0 bg-white p-5 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'>
                <ButtonUI
                  onClick={handleSave}
                  loading={isLoading}
                  disabled={isLoading}
                  size='xl'
                  rounded='xl'
                  variant='contained'
                  color='primary'
                  fontSize='lg'
                  extendedClassName='w-full'
                >
                  {isLoading ? 'Saving...' : 'Confirm Location'}
                </ButtonUI>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
